// The one footer for every public page (Home, Borrower, Distributor,
// Lender) — mounted once in App.js's PublicLayout, the same way SiteHeader
// already is, rather than each page carrying its own copy. Replaces the 4
// separate inline footers those pages used to render (a plain logo + a
// short link row each) with this richer one: brand blurb, three link
// columns, contact details, and a legal-links bottom bar.
//
// Ported from a reference redesign of the Home page — see .sf-* in
// theme.css for the styling.
//
// Terms of Service, Privacy Policy and Refund Policy (2026-08-29) point at
// real pages under src/features/legal/, content pulled from the old
// interestng.com site. About Us now does too, and Newsroom joined it
// (2026-09-01) — both ported from who-we-are.html and newsroom.html.
//
// Every link in this footer now resolves to a real route. Careers used to close
// the Company column pointing at '#'; it was removed rather than left as a
// control that looks live and goes nowhere, and Contact Us took the slot
// (2026-09-01). That mirrors the old site's own footer, whose Company column
// was About Us / Newsroom / Pricing / Contact us — and it is why the bottom bar
// dropped its own Contact Us the same day: with the link in Company, the bar
// repeated it, so that row is now purely legal.

import React from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from './BrandLogo';

// Official accounts. `d` is the 24x24 brand glyph; `c` is the network's own
// colour, applied on hover only — four saturated logos sitting in the footer
// at rest would out-shout the brand mark right above them.
//
// The paths are the standard Simple Icons glyphs. They are long and inlined
// rather than pulled from an icon package on purpose: four paths do not earn a
// dependency, and the footer renders on every public page.
const SOCIALS = [
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/interestng/posts/',
    c: '#0A66C2',
    d: 'M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.35V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zm1.78 13.02H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z',
  },
  {
    name: 'X',
    href: 'https://x.com/INTERESTNGLOAN',
    c: '#000000',
    d: 'M18.9 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.063-6.933zm-1.29 19.49h2.039L6.486 3.24H4.298l13.312 17.404z',
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61593297825428',
    c: '#1877F2',
    d: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/interestngloans/',
    c: '#E4405F',
    d: 'M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06L12 2.16zm0 3.678a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm7.846-10.405a1.44 1.44 0 1 1-2.88 0 1.44 1.44 0 0 1 2.88 0z',
  },
];

export default function SiteFooter() {

  return (
    <footer className="sf-footer">
      <div className="sf-wrap">
        <div className="sf-top">
          <div className="sf-brand">
            <BrandLogo height={22} />
            <p>Borrow better. Distribute smarter. Lend safer. One platform for every side of the loan.</p>
            <div className="sf-social">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Interestng on ${s.name}`}
                  title={s.name}
                  /* Each link carries its OWN brand colour as a custom property
                     so one CSS hover rule serves all four. */
                  style={{ '--sf-social-c': s.c }}
                >
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true" focusable="false">
                    <path d={s.d} />
                  </svg>
                </a>
              ))}
            </div>
          </div>

          <div className="sf-col">
            <h4>Platform</h4>
            <Link to="/borrower">For Borrowers</Link>
            <Link to="/distributor">For Distributors</Link>
            <Link to="/lender">For Lenders</Link>
            <Link to="/#how-it-works">How it Works</Link>
          </div>
          <div className="sf-col">
            <h4>Company</h4>
            <Link to="/#awards">Winners &amp; Accolades</Link>
            <Link to="/about">About Us</Link>
            <Link to="/newsroom">Newsroom</Link>
            <Link to="/contact">Contact Us</Link>
          </div>
          <div className="sf-col">
            <h4>Contact Us</h4>
            <p>✉️ <a href="mailto:tech@interestng.com">tech@interestng.com</a></p>
            <p>🕐 Mon&ndash;Sat, 9 AM&ndash;7 PM IST</p>
            <p>📍 Bengaluru, Karnataka</p>
          </div>
        </div>
        <div className="sf-bottom">
          <span>&copy; 2026 Interestng</span>
          <span className="sf-bottom-links">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/refund-policy">Refund Policy</Link>
          </span>
        </div>
      </div>
    </footer>
  );
}
